import { Dispatch, SetStateAction, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  retrievePropertiesFromApi,
  retrievePropertiesSuitesFromApi,
  retrievePropertySuitesFromApiByRole,
  retrieveServiceProviderPropertiesFromApi,
} from 'src/pages/SharedPageCustomHooks/UseFetchProperties/helpers';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';

interface UseFetchPropertiesParams {
  contactFormData?: Partial<IContactFormData>;
  initialValue?: any;
}

export interface UseFetchPropertiesData {
  errors: Record<string, any>;
  predictions: any[];
  suitesOptions: any[];
  suitesList: any[];
  propertiesList: any[];
  availableProperties: any[];
  availableSuites: any[];
}

export interface UseFetchPropertiesLoading {
  homeownerProperties: boolean;
  propertiesSuites: boolean;
  propertySuite: boolean;
  serviceProviderProperties: boolean;
}

export interface UseFetchPropertiesReturn {
  // State.
  data: UseFetchPropertiesData;
  loading: UseFetchPropertiesLoading;

  // State setter.
  setData: Dispatch<SetStateAction<UseFetchPropertiesData>>;

  // API fetch functions.
  fetchHomeownerProperties: () => Promise<void>;
  fetchPropertiesSuites: (params: any) => Promise<void>;
  fetchPropertySuites: (propertyId: any) => Promise<void>;
  fetchServiceProviderManagerProperties: () => Promise<void>;

  // UI state management functions.
  updateDisplayedSuitesInUIUponRemovingProperty: (
    selectedPropertyToRemove: any
  ) => void;
  updateDisplayedSuitesInUIUponAddingProperty: (
    selectedPropertyToAdd: any
  ) => Promise<void>;
}

const useFetchProperties = ({
  initialValue,
  contactFormData,
}: UseFetchPropertiesParams): UseFetchPropertiesReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [data, setData] = useState({
    errors: {},
    predictions: [],
    suitesOptions: [],
    suitesList: [],
    propertiesList: [],
    availableProperties: [],
    availableSuites: [],
    ...initialValue,
  });

  const [loading, setLoading] = useState({
    homeownerProperties: false,
    propertiesSuites: false,
    propertySuite: false,
    serviceProviderProperties: false,
  });

  const fetchHomeownerProperties = async (): Promise<void> => {
    setLoading((prev: any) => ({ ...prev, homeownerProperties: true }));

    try {
      const propertyData: any = await retrievePropertiesFromApi(user);
      setData((prevState: any) => ({
        ...prevState,
        availableProperties: propertyData,
        propertiesList: contactFormData.id
          ? prevState.propertiesList || []
          : [],
      }));
    } catch (error) {
      console.error('Error in fetchHomeownerProperties:', error);
    } finally {
      setLoading((prev: any) => ({ ...prev, homeownerProperties: false }));
    }
  };

  const fetchPropertiesSuites = async (params: any): Promise<void> => {
    setLoading((prev: any) => ({ ...prev, propertiesSuites: true }));

    try {
      const suitesData: any = await retrievePropertiesSuitesFromApi(params);

      setData((prevState: any) => ({
        ...prevState,
        availableSuites: suitesData,
        suitesList: contactFormData.id ? prevState.suitesList || [] : [],
      }));
    } catch (error) {
      console.error('Error in fetchPropertiesSuites:', error);
    } finally {
      setLoading((prev: any) => ({ ...prev, propertiesSuites: false }));
    }
  };

  const fetchPropertySuites = async (propertyId: any): Promise<void> => {
    setLoading((prev: any) => ({ ...prev, propertyS: true }));

    try {
      const suitesData: any = await retrievePropertySuitesFromApiByRole(
        propertyId,
        user.id,
        user.roles[0].role.name
      );
      setData((prevState: any) => ({
        ...prevState,
        availableSuites: suitesData,
        suitesList: suitesData,
      }));
    } catch (error) {
      console.error('Error in fetchPropertySuites:', error);
    } finally {
      setLoading((prev: any) => ({ ...prev, propertyS: false }));
    }
  };

  const fetchServiceProviderManagerProperties = async (): Promise<void> => {
    setLoading((prev: any) => ({ ...prev, serviceProviderProperties: true }));

    try {
      const propertiesData: any =
        await retrieveServiceProviderPropertiesFromApi(contactFormData.id);

      const propertiesByUser: any = await retrievePropertiesFromApi(user);

      setData((prevState: any) => ({
        ...prevState,
        suitesList: [],
        propertiesList: propertiesData,
        availableProperties: propertiesByUser,
      }));
    } catch (error) {
      console.error('Error in fetchServiceProviderProperties:', error);
    } finally {
      setLoading((prev: any) => ({
        ...prev,
        serviceProviderProperties: false,
      }));
    }
  };

  // Orchestrate removing suites from the UI state for the dropdown menu
  const updateDisplayedSuitesInUIUponRemovingProperty = (
    selectedPropertyToRemove: any
  ): void => {
    const propertyIdToRemove =
      selectedPropertyToRemove.Id || selectedPropertyToRemove.id;

    const currentState = data;

    const filteredAvailableSuites = currentState.availableSuites.filter(
      (suite: any) => suite.propertyId !== propertyIdToRemove
    );

    const filteredSuitesList = currentState.suitesList.filter(
      (suite: any) => suite.propertyId !== propertyIdToRemove
    );

    const updatedState = {
      ...currentState,
      availableSuites: filteredAvailableSuites,
      suitesList: filteredSuitesList,
    };

    setData(updatedState);
  };

  const updateDisplayedSuitesInUIUponAddingProperty = async (
    selectedPropertyToAdd: any
  ) => {
    const propertyIdToAdd =
      selectedPropertyToAdd.Id || selectedPropertyToAdd.id;

    try {
      setLoading((prev: any) => ({ ...prev, propertySuite: true }));

      const newSuitesFromApi: any = await retrievePropertySuitesFromApiByRole(
        propertyIdToAdd,
        user.id,
        user.roles[0].role.name
      );

      const currentState = data;

      const existingSuiteIds = new Set(
        currentState.availableSuites.map((suite: any) => suite.id)
      );

      const uniqueNewSuites = newSuitesFromApi.filter(
        (newSuite: any) => !existingSuiteIds.has(newSuite.id)
      );

      const updatedAvailableSuites = [
        ...currentState.availableSuites,
        ...uniqueNewSuites,
      ];
      const updatedSuitesList = [
        ...currentState.suitesList,
        ...uniqueNewSuites,
      ];

      const updatedState = {
        ...currentState,
        availableSuites: updatedAvailableSuites,
        suitesList: updatedSuitesList,
      };

      setData(updatedState);
    } catch (error) {
      console.error('Error fetching suites for new property:', error);
      const currentState = data;
      const errorMessage = 'Failed to load suites for selected property';
      const updatedErrors = {
        ...currentState.errors,
        property: errorMessage,
      };
      const errorState = {
        ...currentState,
        errors: updatedErrors,
      };
      setData(errorState);
      throw error;
    }
  };

  return {
    data,
    setData,
    loading,
    fetchHomeownerProperties,
    fetchPropertiesSuites,
    fetchPropertySuites,
    fetchServiceProviderManagerProperties,
    updateDisplayedSuitesInUIUponRemovingProperty,
    updateDisplayedSuitesInUIUponAddingProperty,
  };
};

export default useFetchProperties;
